// Button config types
import { RequireOnlyOne } from '@mm/utils';
import { FilterItemOperator } from './AudiencesModel';
import ButtonModel, { WebviewHeightRatio } from './ButtonModel';
import { FormEntry, FormValidationMessage } from './FormsModel';
import TagModel from './TagModel';
import { Editor } from 'react-email-editor';
import { SoldConfigurationsType } from './CampaignsModel';

// Entries types

type TagsWidgetKind = 'add_tags' | 'remove_tags';
export type NotificationWidgetKind =
  | 'email'
  | 'email_sold'
  | 'mobile'
  | 'mark_as_follow_up'
  | 'to_contact';

export type ListAndGalleryEntry = {
  id: number;
  title: string;
  description: string;
  url: string;
  assetId: number | null;
  uploadId: number;
  file: string;
  type: string; // TODO: could add enum
  buttons: ButtonModel[];
  sortOrder: number;
  webviewHeightRatio?: WebviewHeightRatio | null;
};

// Validation Types
export type ValidationMessage = {
  field: string;
  rule: {
    type: string; // TODO: could add enum
    arguments: string | ValidationMessage[];
    value?: string | ValidationMessage[] | null;
  };
};

// Widget configs.

// Works for attachment, audio, video, gif
export type AssetConfig = {
  id: number;
  title: string;
  description: string;
  assetId: number;
  sourceSize: number;
};

export type AttributeWidgetConfig = {
  id: number;
  variableId: number;
  variableType: 'Variable' | 'SystemVariable';
  value: string;
};

export type DelayWidgetConfig = {
  value: number;
  measure: 'minute' | 'hour';
  delaysCount: number;
  tillTime: string;
};

export type OtnWidgetConfig = {
  tag: TagModel;
  pageId: number;
  title: string;
};

export type TagsWidgetConfig = {
  tags: TagModel[];
  kind: TagsWidgetKind;
};

export interface Attachment {
  id?: number;
  uniqId: string;
  name: string;
  added?: boolean;
  deleted?: boolean;
  uploadPromise?: Promise<void>;
  uploadError?: string;
}

export type EmailConfigEntities = {
  id?: number;
  subject: string;
  previewText: string;
  body: string;
  cc: string;
  bcc: string;
  url: string;
  master: boolean;
  status?: 'initial' | 'active' | 'disabled';
  emailAttachments?: Attachment[];
};

export type EmailConfig = {
  id: number;
  aiCustomizer?: boolean;
  subject: string;
  body: string;
  bodyJson?: Parameters<Editor['loadDesign']>['0'];
  bodyDd?: string; // DD stands for Drag and Drop, meaning it is HTML of the email body
  ddEditor?: boolean;
  to: string;
  replyToEmail: string;
  kind: NotificationWidgetKind;
  createdOnTrial: boolean | null;
  markAsFollowUp?: boolean;
  previewText: string;
  emailAttachments?: {
    id: number;
    sourceSize: number;
    originalFileName: string;
  }[];
  emailTotalCount?: number;
  emailOpensCount: number;
  emailQueuedCount: number;
  timeToDeliverAll?: [minTime: number, maxTime: number];
  emailClicksCount: number;
  emailSendsCount: number;
  cc: string;
  bcc: string;
  clicksPercentage: number;
  opensPercentage: number;
  waitingEngagementCount: number;
  entities?: EmailConfigEntities[];
};

export type MobileNotificationConfig = {
  id: number;
  message: string;
  kind: NotificationWidgetKind;
  createdOnTrial: boolean | null;
  companyUserIdsToNotify?: number[];
  markAsFollowUp?: boolean;
};

export type EmailWidgetConfig = EmailConfig | MobileNotificationConfig;

export type GalleryWidgetConfig = {
  id: number;
  imageAspectRatio: string;
  entries: ListAndGalleryEntry[];
};

export type GoToConfig = {
  id: number;
  pageId: number;
  audienceid: number;
};

export type ImageConfig = AssetConfig & {
  description: string;
};

export type ListConfig = {
  id: number;
  topElementStyle: string;
  entries: ListAndGalleryEntry[];
};

export type NavigateConfig = {
  id: number;
  pageId: number;
  oppositePageId?: number | null;
  audienceId: number;
  sendNextAt?: string;
  sendNextAfterMeasure: 'minute' | 'hour';
  sendNextAfterValue: number;
  delaysCount?: number;
  subtype?: 'unicorn_detector';
};

export enum FollowUpType {
  page = 'page',
  reminder = 'reminder',
}

export type QuickQuestionWidgetConfig = {
  id: number;
  variableId: number;
  variableType: 'Variable' | 'SystemVariable';
  body: string;
  allowFreeTextResponse: boolean;
  blocksInput: boolean;
  processedByTriggers: boolean;
  validationRule: string | null;
  response: string;
  sortOrder: number;
  skipSendWidget: boolean;
  buttons: ButtonModel[];
  followUpType: FollowUpType;
  followUpPageId?: number | null;
  followUpTimeout?: number | null;
  followUpTimeoutMeasure?: 'minute' | 'hour';
  followUpText?: string | null;
  additional?: { smsConfirmationMessage?: string };
};

type CustomWebhookOrZapConfig = {
  id: number;
  resourceSubscriptionTriggerName?: string;
  connectionWebhookId?: number | null;
  kind: string;
  firesCount: number;
};

export type SendDataConfig = RequireOnlyOne<
  CustomWebhookOrZapConfig,
  'resourceSubscriptionTriggerName' | 'connectionWebhookId'
>;

export type TextConfig = {
  id: number;
  body: string;
};

export type OptInConfig = {
  id: number;
  body: string;
  legacy: boolean | null;
};

export type TypingConfig = {
  id: number;
  seconds: number;
  createdAt: Date;
  updatedAt: Date;
};

export type UserFormConfig = {
  id: number;
  validationMessages: FormValidationMessage[];
  entries: FormEntry[];
  name: string;
};

export type WidgetConditionalAudience = {
  audienceId: number;
};

export type WidgetConditionalFilter = {
  id?: number;
  variableId: number;
  variableType: 'SystemVariable' | 'Variable';
  operator: FilterItemOperator;
  value: string | string[];
};

export type WidgetConditionalTag = {
  tagName: string;
};

export type WidgetConfig =
  | AssetConfig
  | AttributeWidgetConfig
  | DelayWidgetConfig
  | EmailWidgetConfig
  | GalleryWidgetConfig
  | GoToConfig
  | OptInConfig
  | OtnWidgetConfig
  | ImageConfig
  | ListConfig
  | NavigateConfig
  | QuickQuestionWidgetConfig
  | SendDataConfig
  | TagsWidgetConfig
  | TextConfig
  | TypingConfig
  | UserFormConfig;

export type WidgetConfigHardlyUseful = Partial<
  AssetConfig &
    AttributeWidgetConfig &
    TagsWidgetConfig &
    EmailWidgetConfig &
    GalleryWidgetConfig &
    GoToConfig &
    OptInConfig &
    OtnWidgetConfig &
    ImageConfig &
    ListConfig &
    NavigateConfig &
    SendDataConfig &
    TextConfig &
    QuickQuestionWidgetConfig &
    TypingConfig &
    UserFormConfig &
    DelayWidgetConfig
>;

export type WidgetConfigEvenLessUseful = Partial<
  Partial<AssetConfig> &
    Partial<AttributeWidgetConfig> &
    Partial<TagsWidgetConfig> &
    Partial<EmailWidgetConfig> &
    Partial<GalleryWidgetConfig> &
    Partial<GoToConfig> &
    Partial<OptInConfig> &
    Partial<OtnWidgetConfig> &
    Partial<ImageConfig> &
    Partial<ListConfig> &
    Partial<NavigateConfig> &
    Partial<SendDataConfig> &
    Partial<TextConfig> &
    Partial<QuickQuestionWidgetConfig> &
    Partial<TypingConfig> &
    Partial<UserFormConfig> &
    Partial<DelayWidgetConfig>
>;

export type WidgetBase<Config> = {
  id: number;
  description?: string;
  type: WidgetTypes;
  sortOrder: number;
  pageId: number;
  config: Config;
  buttons: ButtonModel[];
  file: string;
  validated: {
    facebook: boolean;
  };
  validationMessages?: {} | ValidationMessage[];
  metadata?: {};
  conditionalLogicExists: boolean;
  conditionalAudiences?: WidgetConditionalAudience[];
  conditionalFilters?: WidgetConditionalFilter[];
  conditionalTags?: WidgetConditionalTag[];
};

export enum WidgetTypes {
  FORM = 'user_form',
  DELAY = 'delay',
  IMAGE = 'image',
  TEXT = 'text',
  OTN = 'otn',
  LIST = 'list',
  ITEM = 'entry',
  GALLERY = 'gallery',
  VIDEO = 'video',
  EMAIL = 'email',
  NAVIGATE = 'go_to',
  GIF = 'giphy',
  ATTACHMENT = 'attachment',
  AUDIO = 'audio',
  TYPING = 'typing_on',
  ATTRIBUTE = 'attribute',
  TAGS = 'tag',
  SEND_DATA = 'send_data',
  SMS_OPT_IN = 'sms_optin',
  SMS_OPT_IN_V2 = 'sms_optin_v2',
  SMS_OPT_IN_REPLY = 'sms_optin_reply',
  OPT_IN = 'opt_in',
  GOOGLE_SHEETS = 'GOOGLE_SHEETS',
  QUICK_QUESTION = 'quick_question',
  // Note: these one are FE only of limited usage, at least for now.
  QQ_OPTIONS = 'qq_options',
  QQ_EMAIL = 'qq_email',
  QQ_PHONE = 'qq_phone',
  QQ_INFO = 'qq_info',
}

export type QuickQuestionWidgetType =
  | WidgetTypes.OPT_IN
  | WidgetTypes.SMS_OPT_IN
  | WidgetTypes.SMS_OPT_IN_V2
  | WidgetTypes.SMS_OPT_IN_REPLY
  | WidgetTypes.QQ_OPTIONS
  | WidgetTypes.QQ_EMAIL
  | WidgetTypes.QQ_PHONE
  | WidgetTypes.QQ_INFO
  | WidgetTypes.QUICK_QUESTION;

// export type WidgetBaseConfig = {
//   id: number,
//   entries?: any[],
// };

// export default class WidgetModel<T extends WidgetBaseConfig = WidgetConfig> {
// export default class WidgetModel<T extends WidgetConfigHardlyUseful> {
export default interface WidgetModel {
  id: number;
  type: WidgetTypes;
  sortOrder: number;
  pageId: number;
  // config: WidgetConfig;
  config: WidgetConfigEvenLessUseful;
  soldConfigurations: SoldConfigurationsType | null;
  // config: WidgetConfigHardlyUseful;
  smsStructure: {
    body?: string;
    mediaUrl?: string;
  };
  buttons: ButtonModel[];
  file: string;
  validated: {
    facebook: boolean;
    // Note: for now it is FE only to catch BE data inconsistency after dialogue deltes
    buttonDialogueInValid?: boolean;
  };
  validatedByPlatforms: {
    facebook?: boolean;
    instagram?: boolean;
    sms?: boolean;
    webChat?: boolean;
  };
  supportedByPlatforms: {
    facebook: boolean;
    instagram: boolean;
    sms: boolean;
    webChat: boolean;
  };
  needsReview?: boolean; // for automation related indicators
  metadata?: {
    alert?: string; // client side only property to introduce view related errors
    selectedBtnIdx?: number;
    selectedEntryIdx?: number;
    selectedBtnForEntry?: (number | undefined)[];
    formWidgets?: DialogueWidget[];
    previewIndex?: number;
    isPathEmpty?: boolean;
    noSupportedWidgets?: boolean;
  };
  description?: string;
  // TODO: Actual shape is the commented out one below.
  // But it makes TS unhappy in some places. I temporary
  // use simplified version not to make to many changes as
  // part of this already big ticket
  // validationMessages?: {} | ValidationMessage[];
  validationMessages?: ValidationMessage[];
  conditionalLogicExists: boolean;
  conditionalAudiences?: WidgetConditionalAudience[];
  conditionalFilters?: WidgetConditionalFilter[];
  conditionalTags?: WidgetConditionalTag[];
  structure?: any;

  brandingByPlatforms?: {
    facebook: boolean;
    instagram: boolean;
    sms: boolean;
    webChat: boolean;
  };
}

export type WidgetModelBase = {
  id: number;
  type: WidgetTypes;
  buttons: ButtonModel[];
  config: any;
  sortOrder: number;
  pageId: number;
};

export type TextResponse = {
  id: number;
  type: WidgetTypes.TEXT;
  config: {
    body: string;
  };
  buttons: ButtonModel[];
  validated: {
    facebook: boolean;
    buttonDialogueInValid: boolean;
  };
  pageId: undefined;
};

export type DialogueWidget =
  | WidgetModel
  | {
      id: string;
      type: 'user_message';
      message: string;
    }
  | TextResponse;
